// Mətbəx səs siqnalı.
//
// NİYƏ: aşpaz ekrana baxmır — bişirir. Yeni sifariş yalnız vizual görünsə,
// ticket dəqiqələrlə fərq edilmədən qala bilər. Səs mətbəx ekranının ən çox
// gözlənilən funksiyasıdır.
//
// NİYƏ FAYL DEYİL: səs Web Audio ilə SİNTEZ olunur. Səbəb — POS oflayn işləyir
// və mp3 faylı bundle-a əlavə etmək, yükləmə səhvlərini idarə etmək lazım
// gələrdi. Sintez edilən siqnal həmişə mövcuddur, ~0 bayt yer tutur və
// səviyyəsi koddan dəqiq idarə olunur.
//
// BRAUZER SİYASƏTİ: AudioContext istifadəçi jesti olmadan `suspended` başlayır.
// Ona görə ilk kliki/toxunuşu gözləyib `resume()` çağırırıq — əks halda səs
// səssizcə işləməzdi və səbəbi görünməzdi.

import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'kitchen_sound_enabled';

/** İki tonlu qısa zəng — diqqət çəkir, amma qıcıqlandırmır. */
const CHIME: Array<{ freq: number; start: number; duration: number }> = [
  { freq: 880, start: 0, duration: 0.14 },
  { freq: 1320, start: 0.13, duration: 0.22 },
];

function readPreference(): boolean {
  try {
    // Default AÇIQ — mətbəx ekranının mənası budur; istəməyən söndürür.
    return localStorage.getItem(STORAGE_KEY) !== 'off';
  } catch {
    return true;
  }
}

type AudioCtor = typeof AudioContext;

function getAudioCtor(): AudioCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { AudioContext?: AudioCtor; webkitAudioContext?: AudioCtor };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export interface KitchenAlert {
  enabled: boolean;
  toggle: () => void;
  /** Zəngi çal. Söndürülübsə və ya səs dəstəklənmirsə no-op. */
  play: () => void;
}

export function useKitchenAlert(): KitchenAlert {
  const [enabled, setEnabled] = useState<boolean>(readPreference);
  const ctxRef = useRef<AudioContext | null>(null);

  // AudioContext-i LAZY yaradırıq: səhifə açılışında yaratsaq Chrome
  // "AudioContext was not allowed to start" xəbərdarlığı verir.
  const ensureContext = useCallback((): AudioContext | null => {
    if (ctxRef.current) return ctxRef.current;
    const Ctor = getAudioCtor();
    if (!Ctor) return null;
    try {
      ctxRef.current = new Ctor();
      return ctxRef.current;
    } catch {
      return null;
    }
  }, []);

  // İlk istifadəçi jestində context-i oyat. Bir dəfə işləyir, sonra silinir.
  useEffect(() => {
    const unlock = () => {
      const ctx = ensureContext();
      if (ctx && ctx.state === 'suspended') void ctx.resume();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [ensureContext]);

  useEffect(() => {
    return () => {
      void ctxRef.current?.close();
      ctxRef.current = null;
    };
  }, []);

  const play = useCallback(() => {
    if (!enabled) return;
    const ctx = ensureContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();

    const now = ctx.currentTime;
    for (const note of CHIME) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.value = note.freq;

      // Yumşaq başlanğıc/bitiş — kəskin kəsilmə "klik" səsi yaradır.
      const t0 = now + note.start;
      const t1 = t0 + note.duration;
      gain.gain.setValueAtTime(0.0001, t0);
      gain.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, t1);

      osc.connect(gain).connect(ctx.destination);
      osc.start(t0);
      osc.stop(t1 + 0.02);
    }
  }, [enabled, ensureContext]);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
      } catch {
        /* localStorage bloklanıbsa seçim yalnız bu sessiyada qalır */
      }
      // Açılanda dərhal nümunə səsi ver — istifadəçi səviyyəni eşitsin
      // və brauzer jestindən istifadə edib context-i oyadaq.
      if (next) {
        const ctx = ensureContext();
        if (ctx && ctx.state === 'suspended') void ctx.resume();
      }
      return next;
    });
  }, [ensureContext]);

  return { enabled, toggle, play };
}
